import { Countries } from "types/Models/countries";
import { getEntityTypeLabel } from "v2/utils/getEntityTypeLabel";

export type FieldOption = { label: string; value: string };

const PRIORITY_COUNTRY_CODES = ["US", "GB", "IN"];

const countryRank = (code: string): number => {
  const index = PRIORITY_COUNTRY_CODES.indexOf(code);
  return index === -1 ? PRIORITY_COUNTRY_CODES.length : index;
};

export const getPrefixesForType = (
  countries: Countries[] | undefined,
  formationTypeId: string
): { prefix: string; is_default: boolean }[] =>
  countries
    ?.flatMap((country) => country.formation_type)
    .find((type) => type.uuid === formationTypeId)?.prefixes ?? [];

export const getDefaultSuffix = (
  prefixes: { prefix: string; is_default: boolean }[]
): string => (prefixes.find((p) => p.is_default) ?? prefixes[0])?.prefix ?? "";

export const getCountryOptions = (
  countries: Countries[] | undefined
): FieldOption[] =>
  [...(countries ?? [])]
    .sort(
      (a, b) =>
        countryRank(a.code_alpha_2 ?? "") - countryRank(b.code_alpha_2 ?? "")
    )
    .map((country) => ({ label: country.name, value: country.uuid }));

export const getFormationOptions = (
  countries: Countries[] | undefined,
  incorporationCountryId: string
): FieldOption[] =>
  countries
    ?.find((country) => country.uuid === incorporationCountryId)
    ?.formation_type.map((type) => ({ label: type.name, value: type.uuid })) ??
  [];

export const getSelectedEntityTypeOption = (
  options: FieldOption[],
  formationTypeId: string,
  taxedAs: string
): FieldOption | null => {
  const selected = options.find((option) => option.value === formationTypeId);
  if (!selected) return null;

  return {
    ...selected,
    label: getEntityTypeLabel(selected.label, taxedAs, formationTypeId),
  };
};

export const getCountryCodeById = (
  countries: Countries[] | undefined,
  incorporationCountryId: string
): string =>
  countries?.find((country) => country.uuid === incorporationCountryId)
    ?.code_alpha_2 ?? "";

export const getCountryIdForType = (
  countries: Countries[] | undefined,
  formationTypeId: string
): string =>
  countries?.find((country) =>
    country.formation_type.some((type) => type.uuid === formationTypeId)
  )?.uuid ?? "";

export const getCountryCodeForType = (
  countries: Countries[] | undefined,
  formationTypeId: string
): string =>
  countries?.find((country) =>
    country.formation_type.some((type) => type.uuid === formationTypeId)
  )?.code_alpha_2 ?? "";

export const getFormationTypeLabel = (
  countries: Countries[] | undefined,
  formationTypeId: string
): string =>
  countries
    ?.flatMap((country) => country.formation_type)
    .find((type) => type.uuid === formationTypeId)?.name ?? "";

export const buildAddressString = (address: {
  street_address?: string;
  city?: string;
  state?: string;
  zipcode?: string;
}): string =>
  [address.street_address, address.city, address.state, address.zipcode]
    .filter(Boolean)
    .join(", ");
